import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_PROFILE_STAGE_KEYS, INCOME_SOURCE_MANDATORY_KEYS } from '../../common/constants';
import { isStageDisabled } from '../../common/helper';
import { useAppSelector } from '../../common/hooks';
import { IncomeSourcesType } from '../../common/types';
import { IconButton, Typography, BasicButton } from '../../components/atoms';
import { ConfirmationCentered } from '../../components/molecules';
import AddIncomeSourcesDrawer from '../add-income-sources-drawer/index';
import { ReactComponent as CashIcon } from '../../assets/icons/cash.svg';
import { HeaderIcon } from './HeaderIcon';
import { Card } from '../../components/molecules/Card/Card';
import { formatCurrency, getLabelFromAccountType } from '../../utils';
import { ReactComponent as EditIcon } from '../../assets/icons/editIcon.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/deleteIcon.svg';
import { Box } from '@mui/material';
import { TableWithCustomRender } from '../../components/molecules/TableWithCustomRender';
import Dayjs from 'dayjs';
import { isRecordDataValid } from '../../features/advisorConsole/utils';

export function calculateTotalIncome(incomeSources: IncomeSourcesType[]) {
    let annualamount = 0;
    incomeSources.forEach((item: any) => {
        annualamount += +(item.frequency === 'Annually' ? item.amount : item.amount * 12);
    });
    return annualamount;
}

export const IncomeCardFF = () => {
    const [isAddIncomeSourceDrawerOpen, setIsAddIncomeSourceDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
    const [selectedAccountId, setSelectedAccountId] = useState<string>('');
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const { orgInfo } = useAppSelector((state) => state.global);
    const incomeSourceType = orgInfo?.incomeSourceType?.[language] || [];
    const adviceClient = useAppSelector((state) => state.adviceClient);
    const { incomeSources } = adviceClient.accounts;
    const [orderBy, setOrderBy] = useState<'sourceType' | ''>('');
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [deleteModalState, setDeleteModalState] = useState(false);
    const [needPrepopulationOnEdit, setNeedPrepopulationOnEdit] = useState(true);
    const [selectedAccountForDelete, setSelectedAccountForDelete] = useState<string>('');
    const [triggerDelete, setTriggerDelete] = useState<string>('');

    // eslint-disable-next-line
    let amountIncomeSource = 0;
    incomeSources.forEach((item) => {
        amountIncomeSource += item.frequency === 'Annually' ? item.amount : item.amount * 12;
    });

    const isDisabled = isStageDisabled(adviceClient, CLIENT_PROFILE_STAGE_KEYS[2]);

    const headers_ff = [
        {
            label: t('NAME'),
            key: 'sourceName',
            align: 'left' as const,
            width: '35%',
            render: (value: string) => (
                <Typography label={value} color="neutral.grey.500" variant="body.large" sx={{ textAlign: 'left' }} />
            )
        },
        {
            label: t('OWNER'),
            key: 'sourceOwner',
            align: 'left' as const,
            width: '13%',
            render: (value: string) => (
                <Typography
                    label={value === 'primary' ? t('CLIENT') : value === 'joint' ? t('TEXT_JOINT') : t('CO-CLIENT')}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'left' }}
                />
            )
        },
        {
            label: t('TYPE'),
            key: 'sourceType',
            align: 'left' as const,
            width: '13%',
            sortable: true,
            onSortChange: (_key: string, dir: 'asc' | 'desc') => {
                setOrder(dir);
                setOrderBy('sourceType');
            },
            render: (value: string) => (
                <Typography
                    label={getLabelFromAccountType(value, incomeSourceType)}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'left' }}
                />
            )
        },
        {
            label: t('ANNUAL_AMOUNT'),
            key: 'amount',
            align: 'center' as const,
            width: '13%',
            render: (value: number, row: any) => (
                <Typography
                    label={formatCurrency(row.frequency === 'Annually' ? value : value * 12)}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'center' }}
                />
            )
        },
        {
            label: t('ENDING'),
            key: 'endYear',
            align: 'center' as const,
            width: '13%',
            render: (value: string) => (
                <Typography
                    label={value ? Dayjs(value, 'DD-MM-YYYY').format('YYYY') : '-'}
                    color="neutral.grey.500"
                    variant="body.large"
                    sx={{ textAlign: 'center' }}
                />
            )
        },
        {
            label: t('EDIT'),
            key: 'edit',
            align: 'center' as const,
            width: '13%',
            render: (_value: any, row: any) => (
                <Box display="flex" gap={1} justifyContent="center">
                    <IconButton
                        iconName={<EditIcon color="primary" />}
                        bordered={false}
                        onClick={() => handleEditIncomeSource(row.accountId || '')}
                        color="primary"
                    />
                    <IconButton
                        iconName={<DeleteIcon color="primary" />}
                        bordered={false}
                        onClick={() => {
                            setSelectedAccountForDelete(row.accountId || '');
                            setDeleteModalState(true);
                        }}
                        color="primary"
                    />
                </Box>
            )
        }
    ];

    const handleAddIncomeSource = () => {
        setSelectedAccountId('');
        setDrawerMode('add');
        setIsAddIncomeSourceDrawerOpen(true);
    };

    const handleEditIncomeSource = (accountId: string) => {
        setDrawerMode('edit');
        setSelectedAccountId(accountId);
        setIsAddIncomeSourceDrawerOpen(true);
        setNeedPrepopulationOnEdit(false);
    };

    const sortedIncomeSources = useMemo(() => {
        const sourcesClone = [...incomeSources];

        if (!orderBy) return sourcesClone;

        return sourcesClone.sort((a, b) => {
            const valueA = a[orderBy]?.toString().toLowerCase() || '';
            const valueB = b[orderBy]?.toString().toLowerCase() || '';

            if (order === 'asc') {
                return valueA.localeCompare(valueB);
            } else {
                return valueB.localeCompare(valueA);
            }
        });
    }, [incomeSources, order, orderBy]);
    return (
        <Card onClick={() => {}}>
            <div className="goe-client-stepper-card-inner">
                <div className="goe-client-card-header-container">
                    <div className="goe-client-card-header">
                        <div className="goe-client-icon">
                            <HeaderIcon isDisabled={false} size={40} icon={<CashIcon />} />
                        </div>
                        <div className="goe-client-card-header-content">
                            <Typography variant="headers.h4" color="semantic.text.default" label={t('LABEL_INCOME')} />
                            <Typography
                                label={t('INCOME_SOURCES_INFO_TEXT')}
                                color="semantic.text.muted"
                                variant="body.small"
                            />
                        </div>
                    </div>
                    <div className="goe-client-card-header-buttons">
                        <BasicButton
                            disabled={isDisabled}
                            variant="outlined"
                            sx={{ marginRight: '8px' }}
                            onClick={handleAddIncomeSource}
                        >
                            {t('TEXT_ADD_INCOME')}
                        </BasicButton>
                    </div>
                </div>
                {incomeSources.length > 0 && (
                    <TableWithCustomRender
                        headers={headers_ff}
                        dataSource={sortedIncomeSources}
                        conditionalRowFormat={(row) => {
                            const isValid = isRecordDataValid(row, INCOME_SOURCE_MANDATORY_KEYS, true, false);
                            return !isValid
                                ? {
                                      '&.MuiTableRow-root': {
                                          boxShadow: (t: any) => `inset 0 0 0 2px ${t.palette.error[500]}`
                                      }
                                  }
                                : undefined;
                        }}
                    />
                )}
            </div>
            <ConfirmationCentered
                onCancellation={() => setDeleteModalState(false)}
                onCloseIconClick={() => setDeleteModalState(false)}
                onConfirmation={() => {
                    setDeleteModalState(false);
                    setTriggerDelete(selectedAccountForDelete);
                    setSelectedAccountForDelete('');
                }}
                open={deleteModalState}
                content={`${t('DELETE_CONFIRMATION_MODAL_CONTENT')}`}
                confirmButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_DELETE_TEXT')}`}
                cancelButtonLabel={`${t('DELETE_CONFIRMATION_MODAL_CANCEL_TEXT')}`}
                title={t('DELETE_CONFIRMATION_MODAL_HEADER_TEXT')}
                width="396px"
            />
            <AddIncomeSourcesDrawer
                setOpenDrawer={setIsAddIncomeSourceDrawerOpen}
                openDrawer={isAddIncomeSourceDrawerOpen}
                mode={drawerMode}
                selectedAccountId={selectedAccountId}
                triggerDeleteAccountId={triggerDelete}
                onDeleteTriggered={() => setTriggerDelete('')}
                needPrepopulationOnEdit={needPrepopulationOnEdit}
            />
        </Card>
    );
};
